import _ from 'lodash';
import { SnackbarKey } from 'notistack';
import {
    AppStore,
    Closure,
    INotification,
    Product,
    ProductCategory,
    StoreConfiguration,
    Table,
    User
} from '../types';
import { Action, ActionType, ThunkAction } from './actionTypes';
import * as Api from '../Api';

const ERROR_NOTIFICATION: INotification = {
    message: 'Something went wrong with your request',
    key: new Date().getTime(),
    options: { variant: 'error', autoHideDuration: 3000 }
};

const setUser = (user: AppStore['user']): Action => ({
    type: ActionType.SET_USER,
    payload: user
});

const setProductCategories = (categories: AppStore['productCategories']): Action => ({
    type: ActionType.SET_PRODUCT_CATEGORIES,
    payload: categories
});

export const removeNotification = (key: SnackbarKey): Action => ({
    type: ActionType.REMOVE_NOTIFICATION,
    payload: key
});

export const enqueueNotification = (notification: INotification): Action => ({
    type: ActionType.ENQUEUE_NOTIFICATION,
    payload: notification
});

const setCategoryImages = (images: AppStore['categoryImages']): Action => ({
    type: ActionType.SET_CATEGORY_IMAGES,
    payload: images
});

const setTables = (tables: AppStore['tables']): Action => ({
    type: ActionType.SET_TABLES,
    payload: tables
});

const setStoreConfiguration = (
    storeConfiguration: AppStore['storeConfiguration']
): Action => ({
    type: ActionType.SET_STORE_CONFIGURATION,
    payload: storeConfiguration
});

const setAvailableLanguages = (languages: AppStore['availableLanguages']): Action => ({
    type: ActionType.SET_AVAILABLE_LANGUAGES,
    payload: languages
});

const setProducts = (products: AppStore['products']): Action => ({
    type: ActionType.SET_PRODUCTS,
    payload: products
});

const setUsers = (users: AppStore['users']): Action => ({
    type: ActionType.SET_USERS,
    payload: users
});

const setLoading = (loading: AppStore['loading']): Action => ({
    type: ActionType.SET_LOADING,
    payload: loading
});

const setClosures = (closures: AppStore['closures']): Action => ({
    type: ActionType.SET_CLOSURES,
    payload: closures
});

export const fetchAll = (): ThunkAction<Promise<void>> => (dispatch, getState) => {
    const {
        user,
        productCategories,
        products,
        categoryImages,
        tables,
        storeConfiguration,
        availableLanguages,
        users,
        closures
    } = getState().app;

    dispatch(setLoading(true));

    return Promise.all([
        user ? Promise.resolve(user) : Api.getUserAccount(),
        productCategories
            ? Promise.resolve(productCategories)
            : Api.getProductCategories(),
        categoryImages ? Promise.resolve(categoryImages) : Api.getCategoryImages(),
        tables ? Promise.resolve(tables) : Api.getTables(),
        storeConfiguration
            ? Promise.resolve(storeConfiguration)
            : Api.getStoreConfiguration(),
        availableLanguages
            ? Promise.resolve(availableLanguages)
            : Api.getAvailableLanguages(),
        products ? Promise.resolve(products) : Api.getProducts(),
        users ? Promise.resolve(users) : Api.getUsers(),
        closures ? Promise.resolve(closures) : Api.getClosures()
    ])
        .then(([accound, pc, c, t, s, a, p, u, cl]) => {
            dispatch(setUser(accound));
            dispatch(setProductCategories(pc));
            dispatch(setCategoryImages(c));
            dispatch(setTables(t));
            dispatch(setStoreConfiguration(s));
            dispatch(setAvailableLanguages(a));
            dispatch(setProducts(p));
            dispatch(setUsers(u));
            dispatch(setClosures(cl));
        })
        .catch(() => {
            dispatch(enqueueNotification(ERROR_NOTIFICATION));
        })
        .finally(() => {
            dispatch(setLoading(false));
        });
};

export const initialRequest =
    (
        username: string,
        password: string,
        storeId: number
    ): ThunkAction<Promise<unknown>> =>
    async dispatch => {
        try {
            await Api.login(username, password, storeId);
        } catch (e) {
            dispatch(enqueueNotification(ERROR_NOTIFICATION));
            throw new Error(e);
        }

        return dispatch(fetchAll());
    };

export const deleteClosure =
    (id: number): ThunkAction<Promise<void>> =>
    (dispatch, getState) => {
        const { closures } = getState().app;
        const description = closures.find(p => p.id === id)?.shortDescription;

        return Api.deleteClosure({ overrideType: 4, workingHourOverride: { id } }).then(
            () => {
                dispatch(setClosures(closures.filter(p => p.id !== id)));

                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message: `Closure "${description}" deleted successfully`,
                        options: { variant: 'success' }
                    })
                );
            }
        );
    };

export const saveUpdateClosure =
    (closure: Partial<Closure>): ThunkAction<Promise<void>> =>
    (dispatch, getState) => {
        const { closures } = getState().app;

        return Api.saveUpdateClosure({
            overrideType: 4,
            workingHourOverride: {
                ...closure,
                startDate: closure.startDate.toISOString(),
                endDate: closure.endDate.toISOString(),
                effectiveByDate: closure.effectiveByDate.toISOString(),
                closureNotification: closure.closureNotification,
                shortDescription: closure.shortDescription
            }
        })
            .then(updatedClosure => {
                const isNew = _.isNil(closure.id);

                const message = isNew
                    ? `Closure "${closure.shortDescription}" successfully added`
                    : `Closure "${closure.shortDescription}" successfully updated`;

                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message,
                        options: { variant: 'success' }
                    })
                );

                if (isNew) {
                    dispatch(setClosures(closures.concat(updatedClosure)));
                } else {
                    dispatch(
                        setClosures(
                            closures.map(cl =>
                                cl.id === updatedClosure.id ? updatedClosure : cl
                            )
                        )
                    );
                }
            })
            .catch((e: Error) => {
                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message: e.message,
                        options: { variant: 'error' }
                    })
                );
            });
    };

export const saveUpdateProductCategory =
    (productCategory: Partial<ProductCategory>): ThunkAction<Promise<void>> =>
    (dispatch, getState) => {
        const { productCategories } = getState().app;

        return Api.saveUpdateProductCategory(productCategory)
            .then(updatedCategory => {
                const isNew = _.isNil(productCategory.id);
                const message = isNew
                    ? `Category "${productCategory.name}" successfully added`
                    : `Category "${productCategory.name}" successfully updated`;

                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message,
                        options: { variant: 'success' }
                    })
                );

                if (isNew) {
                    dispatch(
                        setProductCategories(productCategories.concat(updatedCategory))
                    );
                } else {
                    dispatch(
                        setProductCategories(
                            productCategories.map(category =>
                                category.id === updatedCategory.id
                                    ? updatedCategory
                                    : category
                            )
                        )
                    );
                }
            })
            .catch((e: Error) => {
                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message: e.message,
                        options: { variant: 'error' }
                    })
                );
            });
    };

export const saveUpdateProduct =
    (product: Partial<Product>): ThunkAction<Promise<void>> =>
    (dispatch, getState) => {
        const { products } = getState().app;

        return Api.saveUpdateProduct(product)
            .then(updatedProduct => {
                const isNew = _.isNil(product.id);
                const message = isNew
                    ? `Product "${product.name}" successfully added`
                    : `Product "${product.name}" successfully updated`;

                if (isNew) {
                    dispatch(setProducts(products.concat(updatedProduct)));
                } else {
                    dispatch(
                        setProducts(
                            products.map(p =>
                                p.id === updatedProduct.id ? updatedProduct : p
                            )
                        )
                    );
                }

                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message,
                        options: { variant: 'success' }
                    })
                );
            })
            .catch((e: Error) => {
                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message: e.message,
                        options: { variant: 'error' }
                    })
                );
            });
    };

export const deleteProduct =
    (id: number): ThunkAction<Promise<void>> =>
    (dispatch, getState) => {
        const { products } = getState().app;
        const prodName = products.find(p => p.id === id)?.name;

        return Api.deleteProduct(id).then(() => {
            dispatch(setProducts(products.filter(p => p.id !== id)));

            dispatch(
                enqueueNotification({
                    key: new Date().getTime(),
                    message: `Product "${prodName}" deleted successfully`,
                    options: { variant: 'success' }
                })
            );
        });
    };

export const saveUpdateTable =
    (table: Partial<Table>): ThunkAction<Promise<void>> =>
    (dispatch, getState) => {
        const { tables } = getState().app;

        return Api.saveUpdateTable(table).then(updatedTable => {
            const existingTableIdx = tables.findIndex(
                ({ id }) => id === updatedTable.id
            );

            if (existingTableIdx < 0) {
                dispatch(setTables(tables.concat(updatedTable)));
                return;
            }

            dispatch(
                setTables(
                    tables.map(t => (t.id === updatedTable.id ? updatedTable : t))
                )
            );
        });
    };

export const updateStoreConfiguration =
    (data: Partial<StoreConfiguration>): ThunkAction<Promise<StoreConfiguration>> =>
    dispatch =>
        Api.updateStoreConfiguration(data)
            .then(storeConfiguration => {
                dispatch(setStoreConfiguration(storeConfiguration));

                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message: 'Store configuration updated successfully',
                        options: { variant: 'success' }
                    })
                );

                return storeConfiguration;
            })
            .catch(() => {
                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message:
                            'Store configuration was changed by someone else! Please try again',
                        options: { variant: 'warning' }
                    })
                );

                return Api.getStoreConfiguration();
            });

export const generatePDF =
    (data): ThunkAction<Promise<unknown>> =>
    () =>
        Api.generatePDF(data).then(res => {
            if (res.status === 400) {
                return res.text().then(err => {
                    throw new Error(err);
                });
            }

            return null;
        });

export const resetPassword =
    (values): ThunkAction<Promise<unknown>> =>
    () =>
        Api.resetPassword(values).then(res => {
            if (res.status === 200) {
                return res.text();
            }

            throw new Error('Unexpected error!');
        });

export const saveUpdateUser =
    (user: Partial<User>): ThunkAction<Promise<void>> =>
    (dispatch, getState) => {
        const { users } = getState().app;

        return Api.saveUpdateUser(user)
            .then(updatedUser => {
                const isNew = _.isNil(user.id);
                const message = isNew
                    ? `User "${user.userName}" successfully added`
                    : `User "${user.userName}" successfully updated`;

                if (isNew) {
                    dispatch(setUsers(users.concat(updatedUser)));
                } else {
                    dispatch(
                        setUsers(
                            users.map(u => (u.id === updatedUser.id ? updatedUser : u))
                        )
                    );
                }

                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message,
                        options: { variant: 'success' }
                    })
                );
            })
            .catch((e: Error) => {
                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message: e.message,
                        options: { variant: 'error' }
                    })
                );
            });
    };
